import { apiFetch } from '@/utilities/api'
import { verifyResult } from '@/utilities/util'
import { toast } from '@/utilities/toast'

// ============================================================================
// Shared Types
// ============================================================================

/**
 * Standard options for mutation hooks.
 * Provides optional onSuccess/onError callbacks.
 */
export interface MutationOptions {
  onSuccess?: (data?: any) => void
  onError?: (error: Error) => void
}

// ============================================================================
// API Helpers
// ============================================================================

/**
 * Creates a mutation API call using apiFetch.
 *
 * @param endpoint - The API endpoint URL
 * @param method - HTTP method (GET, POST, PUT, DELETE)
 * @param body - Request body payload (null for GET requests)
 * @returns Promise that resolves with the response data or rejects with an error
 *
 * @example
 * ```ts
 * const result = await createMutationCall<CreateGroupResult>(
 *   `${apis.apiDatabase.uri}group/add`,
 *   'POST',
 *   { label: 'My Group', mapID: 123 }
 * )
 * ```
 */
interface MutationCallOptions {
  /** When true, suppresses automatic toast notifications. Useful for
   *  background fetches, cancel/revert operations, or intermediate steps
   *  in multi-step flows. Defaults to false (toasts are shown). */
  silent?: boolean
}

export const createMutationCall = <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body: unknown,
  options?: MutationCallOptions
): Promise<T> => {
  const showToast = !options?.silent

  return new Promise((resolve, reject) => {
    apiFetch(endpoint, method, body as Record<string, unknown>, result => {
      if (verifyResult(result)) {
        // Show server-provided success message as toast (matches AsyncFetch behavior)
        if (showToast && result.message) {
          toast.success(result.message)
        }
        // Normalize response - prefer result.data if available, otherwise use full result
        resolve((result.data ?? result) as T)
      } else {
        const errorMessage =
          result.message ||
          (result.error instanceof Error
            ? result.error.message
            : result.error) ||
          `Failed to ${method} ${endpoint}`
        // Show error message as toast (matches AsyncFetch behavior)
        if (showToast) {
          toast.error(errorMessage)
        }
        reject(new Error(errorMessage))
      }
    })
  })
}
